import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink } from 'lucide-react';
import { SITE_URL } from '../data/seoPages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ value }) => (
  <span className="inline-flex" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= Math.round(value) ? 'text-[#d4af37] fill-[#d4af37]' : 'text-gray-600'}`} aria-hidden="true" />
    ))}
  </span>
);

export const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews/google`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => {
    if (!data?.rating || !data?.user_ratings_total) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-rating-ld', 'true');
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#business`,
      name: 'SG Cabs',
      url: `${SITE_URL}/`,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: data.rating,
        reviewCount: data.user_ratings_total,
        bestRating: 5,
        worstRating: 1,
      },
      review: data.reviews.filter((r) => r.text).slice(0, 5).map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author || 'Google user' },
        reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 },
        reviewBody: r.text,
        datePublished: r.publish_time ? r.publish_time.slice(0, 10) : undefined,
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [data]);

  if (!data?.rating) return null;

  return (
    <div data-testid="google-reviews" className="mb-16">
      <div className="lux-card rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 mb-8">
        <div className="flex items-center gap-4">
          <div className="font-display text-5xl font-semibold text-[#d4af37]" data-testid="google-rating">{data.rating.toFixed(1)}</div>
          <div>
            <Stars value={data.rating} />
            <p className="text-sm text-gray-400 mt-1" data-testid="google-rating-count">{data.user_ratings_total} Google ratings</p>
          </div>
        </div>
        <div className="sm:ml-auto flex items-center gap-3">
          <img src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png" alt="Google" className="w-6 h-6" />
          <span className="text-sm text-gray-300">Rating &amp; reviews from Google</span>
          {data.google_maps_uri && (
            <a href={data.google_maps_uri} target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline text-sm inline-flex items-center">
              Write a review <ExternalLink className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>

      {data.reviews.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.filter((r) => r.text).slice(0, 6).map((r, i) => (
            <article key={r.uri || i} className="lux-card rounded-2xl p-6 flex flex-col" data-testid={`google-review-${i}`}>
              <Stars value={r.rating} />
              <p className="text-gray-300 text-sm leading-relaxed mt-3 mb-4 flex-1">"{r.text}"</p>
              <div className="flex items-center gap-3">
                {r.author_photo ? (
                  <img src={r.author_photo} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-[#d4af37]/20 text-[#d4af37] flex items-center justify-center font-semibold text-sm">{(r.author || 'G')[0]}</div>
                )}
                <div>
                  <p className="text-white text-sm font-medium">{r.author || 'Google user'}</p>
                  <p className="text-xs text-gray-500">{r.relative_time}</p>
                </div>
                {r.uri && <a href={r.uri} target="_blank" rel="noopener noreferrer" className="ml-auto text-gray-500 hover:text-[#d4af37]" aria-label="View on Google"><ExternalLink className="w-4 h-4" /></a>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
